import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentPage from '../../payment-page';
import './styles.scss';

interface CustomPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onPaymentSuccess: (response: any) => void;
  onPaymentFailure?: (errorMessage: string) => void;
  amount: number;
  planName: string;
  planType: string;
  billingCycle: string;
  email: string;
  homeownerId: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    phoneNumber?: string;
  };
  planData?: any;
}

export const CustomPaymentModal: React.FC<CustomPaymentModalProps> = ({
  open,
  onClose,
  onPaymentSuccess,
  onPaymentFailure,
  amount,
  planName,
  planType,
  billingCycle,
  email,
  homeownerId,
  customerInfo,
  planData,
}) => {
  const [paymentFormSessionKey, setPaymentFormSessionKey] = useState(0);

  useEffect(() => {
    if (open) {
      setPaymentFormSessionKey((prev) => prev + 1);
    }
  }, [open]);

  const handlePaymentSuccess = (response: any) => {
    onPaymentSuccess(response);
    onClose();
  };

  const handlePaymentFailure = (errorMessage: string) => {
    onPaymentFailure?.(errorMessage);
    // Don't close on failure, let user retry
  };

  // Debug: Log the incoming plan data

  // Check if planData is already a proper Plan object (from plan context)
  // or if it's the simplified object (from selectedPlanData)
  const isPlanObject = planData && planData._id && planData.pricing;
  
  const transformedPlan = planData ? (isPlanObject ? planData : {
    // Transform simplified planData to proper Plan object structure
    _id: planData.id || planData._id || '',
    slug: planData.slug || planData.name?.toLowerCase().replace(/\s+/g, '-') || '',
    name: planData.name || '',
    description: planData.description || '',
    tagLine: planData.tagLine || planData.name || '',
    benefits: planData.benefits || [],
    pricing: {
      price: planData.price || amount || 0,
      originalPrice: planData.originalPrice || null,
      currency: planData.currency || 'USD',
      period: planData.billingCycle === 'monthly' ? 'monthly' : 'annually',
      singlePaymentForDuration: planData.singlePaymentForDuration || false,
      subscription: planData.subscription || null
    },
    public: planData.public !== undefined ? planData.public : true,
    perks: planData.perks || planData.benefits || [],
    primaryPlan: planData.primaryPlan || false,
    highlevelProductId: planData.highlevelProductId || '',
    highlevelPriceId: planData.highlevelPriceId || '',
    metadata: planData.metadata || {}
  }) : undefined;


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="custom-payment-modal"
      PaperProps={{
        className: 'custom-payment-modal-paper',
      }}
    >
      <DialogTitle className="custom-payment-modal-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>Complete Your Payment</span>
          <IconButton
            onClick={onClose}
            size="small"
            className="close-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent className="custom-payment-modal-content">
        {open ? (
          <PaymentPage
            key={paymentFormSessionKey}
            amount={amount}
            planName={planName}
            planType={planType}
            billingCycle={billingCycle}
            email={email}
            homeownerId={homeownerId}
            customerInfo={customerInfo}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFailure={handlePaymentFailure}
            plan={transformedPlan}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
